import { useQuery } from '@apollo/client/react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Check, ChevronDown, History, Info, Minus, User } from 'lucide-react';

import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  buildModuleHistory,
  groupModuleHistory,
} from '@modules/users/userModuleHistory';
import {
  GET_MODULE_HISTORY_ACTORS,
  GET_USER_MODULE_HISTORY,
} from '@modules/users/userQueries';
import { useLocale } from '@shared/hooks/locale';
import { getModuleLabel } from '@shared/utils/module-label';

export const UserModuleHistoryDialog = ({
  open,
  userId,
  onClose,
}: {
  open: boolean;
  userId: string;
  onClose: () => void;
}) => {
  const { t } = useLocale();
  const { me } = useAppData();
  if (me?.is_admin !== true) {
    return null;
  }
  return (
    <SlideDialog
      open={open}
      onClose={onClose}
      cancelText={t('Close')}
      maxWidth="sm"
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
        },
        '& .MuiDialogTitle-root': {
          flex: 1,
          minWidth: 0,
          mr: 1,
        },
        '& .MuiDialogTitle-root + .MuiIconButton-root': {
          flexShrink: 0,
        },
      }}
      title={
        <Stack
          component="span"
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', minWidth: 0 }}
        >
          <History size={20} aria-hidden style={{ flexShrink: 0 }} />
          <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {t('Module history')}
          </Box>
        </Stack>
      }
    >
      <UserModuleHistoryContent key={userId} userId={userId} />
    </SlideDialog>
  );
};

const UserModuleHistoryContent = ({ userId }: { userId: string }) => {
  const { t, locale } = useLocale();
  const { data, previousData, loading, error, refetch } = useQuery(
    GET_USER_MODULE_HISTORY,
    {
      variables: { id: userId },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    },
  );
  const historyUser = (data ?? previousData)?.users_by_pk;
  const moduleGrants = historyUser?.module_grants ?? [];
  const moduleEvents = buildModuleHistory(
    historyUser?.activities ?? [],
    moduleGrants,
  );
  const statusKnown = !loading && !error && data?.users_by_pk != null;
  const groups = groupModuleHistory(
    moduleEvents,
    moduleGrants.map(grant => grant.module_id),
    id => getModuleLabel(id, t),
    locale,
  );
  const actorIds = [
    ...new Set(
      moduleEvents.flatMap(event =>
        event.created_by != null ? [event.created_by] : [],
      ),
    ),
  ];
  const {
    data: actorData,
    error: actorsError,
    refetch: refetchActors,
  } = useQuery(GET_MODULE_HISTORY_ACTORS, {
    variables: { ids: actorIds },
    fetchPolicy: 'no-cache',
    skip: actorIds.length === 0,
  });
  const actors = new Map(
    actorsError ? [] : actorData?.users.map(actor => [actor.id, actor]),
  );
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
  const actorLabel = (id: string | null | undefined, label?: string) => {
    const actor = id ? actors.get(id) : undefined;
    return id ? (
      <Link
        href={`/users/${id}`}
        target="_blank"
        rel="noreferrer"
        color="inherit"
        underline="hover"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          maxWidth: '100%',
          overflowWrap: 'anywhere',
        }}
      >
        <Avatar
          src={actor?.user_images[0]?.image.url ?? undefined}
          sx={{ width: 24, height: 24 }}
        >
          <User size={14} aria-hidden />
        </Avatar>
        <Typography variant="body2" component="span" sx={{ minWidth: 0 }}>
          {label ?? (actor?.full_name || id)}
        </Typography>
      </Link>
    ) : (
      <Typography variant="body2">{label ?? t('Unknown user')}</Typography>
    );
  };
  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack spacing={2}>
        {actorsError && (
          <Alert
            severity="warning"
            action={
              <Button onClick={() => void refetchActors().catch(() => {})}>
                {t('Retry')}
              </Button>
            }
          >
            {t('Could not load user names. User IDs are shown instead.')}
          </Alert>
        )}
        {groups.map(({ moduleId, active, events }, index) => (
          <Box
            component="details"
            key={moduleId}
            open={index === 0}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              overflowWrap: 'anywhere',
              '&[open] > summary .module-history-chevron': {
                transform: 'rotate(180deg)',
              },
            }}
          >
            <Box
              component="summary"
              sx={{
                p: 2,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                listStyle: 'none',
                '&::-webkit-details-marker': { display: 'none' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              <Typography
                component="span"
                variant="body2"
                sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}
              >
                {getModuleLabel(moduleId, t)}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                color={statusKnown && active ? 'success' : 'default'}
                icon={
                  statusKnown && active ? (
                    <Check size={12} />
                  ) : (
                    <Minus size={12} />
                  )
                }
                label={
                  !statusKnown
                    ? t('Unknown')
                    : active
                    ? t('Active')
                    : t('Inactive')
                }
                sx={{ flexShrink: 0, height: 22, fontSize: 12 }}
              />
              <ChevronDown
                className="module-history-chevron"
                size={18}
                aria-hidden
                style={{ flexShrink: 0 }}
              />
            </Box>
            <Box sx={{ px: 2, pb: 2 }} aria-busy={loading}>
              {loading && <LoadingSpinner sx={{ p: 1 }} />}
              {error && (
                <Alert
                  severity="error"
                  action={
                    <Button onClick={() => void refetch().catch(() => {})}>
                      {t('Retry')}
                    </Button>
                  }
                >
                  {t('Could not load module history')}
                </Alert>
              )}
              {!loading && !error && data?.users_by_pk == null && (
                <Alert severity="warning">
                  {t('Module history is unavailable for this user')}
                </Alert>
              )}
              {statusKnown && events.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('No access history')}
                </Typography>
              )}
              <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {events.map(event => {
                  const at = event.created_at;
                  const actorId = event.created_by;
                  const actorName =
                    (actorId
                      ? actors.get(actorId)?.full_name || actorId
                      : null) ?? t('Unknown user');
                  const beforeTracking = event.before_tracking;
                  const beforeTrackingDescription = t(
                    'This module was already active when history tracking started. Its original cause and time are unknown.',
                  );
                  return (
                    <Stack
                      component="li"
                      key={event.id}
                      spacing={0.75}
                      sx={{ py: 1.5, borderTop: 1, borderColor: 'divider' }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ alignItems: 'center' }}
                      >
                        {beforeTracking ? (
                          <>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {t('Before tracking')}
                            </Typography>
                            <Tooltip title={beforeTrackingDescription} arrow>
                              <Box
                                component="button"
                                type="button"
                                aria-label={beforeTrackingDescription}
                                sx={{
                                  display: 'inline-flex',
                                  flexShrink: 0,
                                  p: 0.5,
                                  border: 0,
                                  bgcolor: 'transparent',
                                  color: 'text.secondary',
                                  cursor: 'help',
                                }}
                              >
                                <Info size={14} aria-hidden />
                              </Box>
                            </Tooltip>
                          </>
                        ) : (
                          actorLabel(
                            actorId,
                            event.type === 'grant'
                              ? t('Granted by {{actor}}', { actor: actorName })
                              : t('Revoked by {{actor}}', {
                                  actor: actorName,
                                }),
                          )
                        )}
                      </Stack>
                      {at && (
                        <Typography variant="caption" color="text.secondary">
                          {dateFormatter.format(new Date(at))}
                        </Typography>
                      )}
                    </Stack>
                  );
                })}
              </Box>
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
